import React, { useEffect, useRef, useState } from 'react';
import styles from './Joystick.module.scss';
import emitter from '../../emitter';

const Joystick = () => {
    const joystickRef = useRef(null);
    const knobRef = useRef(null);
    const [active, setActive] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [normalizedPosition, setNormalizedPosition] = useState({ x: 0, y: 0 });
    const lastPositionRef = useRef({ x: 0, y: 0 });
    const touchIdRef = useRef(null);

    useEffect(() => {
        const handleTouchStart = (e) => {
            if (!active && joystickRef.current) {
                const touch = e.touches[0];
                touchIdRef.current = touch.identifier;
                setActive(true);

                // Ne pas repositionner le joystick, seul le knob se déplace
                const rect = joystickRef.current.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;

                let x = touch.clientX - centerX;
                let y = touch.clientY - centerY;

                // Limiter le déplacement du knob à l'intérieur du joystick
                const radius = rect.width / 2;
                const distance = Math.sqrt(x * x + y * y);

                if (distance > radius) {
                    x = (x / distance) * radius;
                    y = (y / distance) * radius;
                }

                // Position initiale du knob
                setPosition({ x, y });

                // Émettre la position normalisée pour continuer le mouvement
                const normalizedX = x / radius;
                const normalizedY = y / radius;
                lastPositionRef.current = { x: normalizedX, y: normalizedY };
                emitter.emit('joystick:move', {
                    x: normalizedX,
                    y: normalizedY
                });
            }
        };

        const handleTouchMove = (e) => {
            if (active && joystickRef.current && knobRef.current) {
                // Trouver le bon touch event
                let touchEvent = null;
                for (let i = 0; i < e.touches.length; i++) {
                    if (e.touches[i].identifier === touchIdRef.current) {
                        touchEvent = e.touches[i];
                        break;
                    }
                }

                if (!touchEvent) return;

                const rect = joystickRef.current.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;

                // Calcul de la position du knob par rapport au centre du joystick
                let x = touchEvent.clientX - centerX;
                let y = touchEvent.clientY - centerY;

                // Limiter le déplacement du knob à l'intérieur du joystick
                const radius = rect.width / 2;
                const distance = Math.sqrt(x * x + y * y);

                if (distance > radius) {
                    x = (x / distance) * radius;
                    y = (y / distance) * radius;
                }

                // Normaliser les valeurs entre -1 et 1
                const normalizedX = x / radius;
                const normalizedY = y / radius;

                setPosition({ x, y });
                setNormalizedPosition({ x: normalizedX, y: normalizedY });

                // Sauvegarder la dernière position
                lastPositionRef.current = { x: normalizedX, y: normalizedY };

                // Émettre la position du joystick pour le contrôle de la caméra
                emitter.emit('joystick:move', { x: normalizedX, y: normalizedY });
            }
        };

        const handleTouchEnd = (e) => {
            // Vérifier si c'est le bon touch qui se termine
            let touchEnded = false;
            for (let i = 0; i < e.changedTouches.length; i++) {
                if (e.changedTouches[i].identifier === touchIdRef.current) {
                    touchEnded = true;
                    break;
                }
            }

            if (active && touchEnded) {
                setActive(false);
                // Réinitialiser la position visuelle du knob mais garder la dernière position pour la caméra
                setPosition({ x: 0, y: 0 });
                touchIdRef.current = null;

                // Ne pas émettre d'événement avec une position à zéro pour maintenir la dernière position
                // emitter.emit('joystick:move', { x: 0, y: 0 });
            }
        };

        document.addEventListener('touchstart', handleTouchStart);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
        document.addEventListener('touchcancel', handleTouchEnd);

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            document.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [active]);

    return (
        <div
            className={`${styles.joystick} ${active ? styles.active : ''}`}
            ref={joystickRef}
        >
            <div
                className={styles.knob}
                ref={knobRef}
                style={{
                    transform: `translate(${position.x}px, ${position.y}px)`
                }}
            ></div>
        </div>
    );
};

export default Joystick; 